import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  PlayCircle,
  ClipboardList,
  Wrench,
  Boxes,
  ShieldAlert,
  FileText,
  UserPlus,
  BarChart3,
  PenTool,
  LayoutList,
  LogOut,
  Crown,
  Search,
  Radar,
  LifeBuoy,
} from "lucide-react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AIAssistant from "@/components/AIAssistant";
import NotificationCenter from "@/components/NotificationCenter";
import SiteSwitcher from "@/components/SiteSwitcher";
import SupportModal from "@/components/SupportModal";
import BackupStatusBar from "@/components/BackupStatusBar";
import { can } from "@/lib/permissions";

// Primary nav — kept ultra-lean to mirror the mobile app.
// Everything else lives one click deep, off the SWING home page's Action button.
const nav = [
  { to: "/", label: "Swing", icon: PlayCircle, testid: "nav-swing", cap: "swing" },
  { to: "/command-centre", label: "Command Centre", icon: Radar, testid: "nav-command-centre", cap: "crewDashboard" },
  { to: "/shift-summary", label: "Shift Summary", icon: LayoutList, testid: "nav-shift-summary", cap: "swing" },
  { to: "/inspections", label: "Inspections", icon: FileText, testid: "nav-inspections", cap: "ownInspections" },
  { to: "/handovers", label: "Operational Handover", icon: ClipboardList, testid: "nav-handovers", cap: "handovers" },
  { to: "/breakdowns", label: "Asset Breakdowns", icon: Wrench, testid: "nav-breakdowns", cap: "handovers" },
  { to: "/ppfs", label: "Planning Requests", icon: PenTool, testid: "nav-ppfs", cap: "ppfsWrite" },
  { to: "/hazards", label: "Site Hazards", icon: ShieldAlert, testid: "nav-hazards", cap: "handovers" },
  { to: "/assets", label: "Assets", icon: Boxes, testid: "nav-assets", cap: "assetsRead" },
  { to: "/fault-finder", label: "Fault Finder", icon: Search, testid: "nav-fault-finder", cap: "faultFinder" },
  { to: "/insights", label: "Insights", icon: BarChart3, testid: "nav-insights", cap: "insights" },
  { to: "/owner", label: "Owner Center", icon: Crown, testid: "nav-owner", cap: "ownerCentre" },
  { to: "/register", label: "Register", icon: UserPlus, testid: "nav-register", cap: "registerUser" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [now, setNow] = useState(new Date());
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Cinematic — scroll-tracked ambient spotlight. Cheap: only sets two
  // CSS vars on <html> once per rAF so the fixed body::before gradient
  // recalculates paint (no reflow).
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        const y = h > 0 ? Math.min(1, Math.max(0, window.scrollY / h)) : 0;
        document.documentElement.style.setProperty("--spot-y", String(0.15 + y * 0.55));
      });
    };
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        // Subtle mouse-x nudge for the ambient background spotlight ONLY.
        // No card-hover glow — users found the pointer trail distracting.
        const w = window.innerWidth || 1;
        const mx = (e.clientX / w) * 40 + 30; // 30..70%
        document.documentElement.style.setProperty("--spot-x", `${mx}%`);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Route transition — a soft orange sweep plays over the page for ~700ms
  // whenever the pathname changes. The class is toggled on <body> so it
  // covers the whole viewport (drawer + main + everything).
  useEffect(() => {
    document.body.classList.remove("route-sweep");
    // Force a reflow to restart the animation
    void document.body.offsetWidth;
    document.body.classList.add("route-sweep");
    const t = setTimeout(() => document.body.classList.remove("route-sweep"), 720);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Aurora scanner sweep — 2px orange strip across the top of every page */}
      <div className="aurora-sweep no-print" aria-hidden="true" />
      {/* Digger backdrop — tuned brighter so the machine is a proud backdrop,
          not a whisper. Josh (Feb 9) asked for the digger to feel more
          present across every page — bumped opacity 0.5 → 0.7 and lightened
          the top of the overlay gradient so the machine reads through. */}
      <div
        className="fixed inset-0 pointer-events-none z-0 no-print"
        aria-hidden="true"
        style={{
          backgroundImage: "url('/digger-bg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center 55%",
          backgroundRepeat: "no-repeat",
          opacity: 0.7,
        }}
      />
      <div
        className="fixed inset-0 pointer-events-none z-0 no-print"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.32) 0%, rgba(10,10,10,0.62) 55%, rgba(10,10,10,0.78) 100%)",
        }}
      />
      {/* Ambient: fine grid + slow-rotating orange spotlight + subtle scan line */}
      <div className="sl-ambient-grid no-print" aria-hidden="true" />
      <div className="sl-ambient-spotlight no-print" aria-hidden="true" />
      <div className="sl-ambient-scan no-print" aria-hidden="true" />

      {/* Top PIT LINK marker bar — thick, glowing, gradient stripe for standout */}
      <div
        className="relative no-print z-10 overflow-hidden"
        style={{
          height: 8,
          background:
            "linear-gradient(90deg, #b95a10 0%, #f58220 20%, #ffb347 50%, #f58220 80%, #b95a10 100%)",
          boxShadow:
            "0 0 24px rgba(245,130,32,0.6), 0 0 4px rgba(245,130,32,0.9), inset 0 -1px 0 rgba(0,0,0,0.35)",
        }}
        aria-hidden="true"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "repeating-linear-gradient(135deg, rgba(0,0,0,0.14) 0 8px, transparent 8px 20px)",
            mixBlendMode: "multiply",
          }}
        />
        {/* Slow light-streak sweep across the marker */}
        <div className="sl-marker-sweep" />
      </div>

      {/* Top bar — glass */}
      <header
        className="sticky top-0 z-40 no-print topbar-glass"
        data-testid="app-topbar"
      >
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-24 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img
              src="/shiftlog-logo.png?v=pl2"
              alt="PIT LINK"
              className="select-none pointer-events-none sl-logo-glow"
              style={{
                height: 80,
                width: "auto",
                background: "transparent",
                border: "none",
                boxShadow: "none",
              }}
              draggable="false"
              data-testid="topbar-logo"
            />
            <div className="hidden lg:block pl-4" style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="font-display text-sm font-black tracking-tight leading-none">
                Maintenance Hub
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.28em] mt-1 font-bold"
                style={{ color: "var(--primary)" }}
              >
                Field Edition · v1.0
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <BackupStatusBar />
            <SiteSwitcher />

            <NotificationCenter />
            <AIAssistant.Trigger />
            <button
              type="button"
              onClick={() => setSupportOpen(true)}
              title="Technical Support"
              aria-label="Technical Support"
              className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all hover:translate-y-[-1px]"
              style={{
                background: "rgba(245,130,32,0.10)",
                border: "1px solid rgba(245,130,32,0.35)",
                color: "var(--primary)",
                boxShadow: "0 6px 18px -12px rgba(245,130,32,0.55)",
              }}
              data-testid="topbar-support-btn"
            >
              <LifeBuoy className="w-5 h-5" />
            </button>

            <div
              className="hidden md:flex flex-col items-end data-num text-sm"
              data-testid="topbar-clock"
            >
              <span className="font-black text-base text-glow-white">
                {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
              <span className="text-muted text-[10px] uppercase tracking-widest">
                {now.toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short" })}
              </span>
            </div>

            <Link
              to="/profile"
              className="pill pill-orange hidden sm:inline-flex hover:opacity-80"
              data-testid="topbar-user-role"
            >
              {user?.role || "user"}
            </Link>
            <Link
              to="/profile"
              className="text-right hidden sm:block hover:opacity-80 transition-opacity"
              data-testid="topbar-user"
            >
              <div className="font-display font-black text-sm leading-none">
                {user?.name || user?.full_name || user?.username}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted mt-1">
                {user?.site_code || user?.email || user?.username}
              </div>
            </Link>
            <button
              className="btn btn-sm btn-ghost"
              data-testid="btn-logout"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex relative z-10">
        {/* Side nav (desktop) — glass. Scrollable so the bottom items stay
            reachable on short viewports (13" laptops, split-screen). */}
        <aside
          className="hidden md:flex flex-col w-60 sticky top-[81px] self-start no-print sidebar-glass"
          style={{
            borderRight: "1px solid rgba(255,255,255,0.06)",
            height: "calc(100vh - 85px)",
          }}
          data-testid="sidebar"
        >
          <nav
            className="flex-1 min-h-0 overflow-y-auto flex flex-col p-3 gap-1 stagger-in sidebar-scroll"
            data-testid="sidebar-nav"
          >
            {nav
              .filter((n) => !n.cap || can(user, n.cap))
              .map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 font-display font-black uppercase tracking-wider text-sm rounded-xl transition ${
                      isActive
                        ? "text-black nav-active-accent"
                        : "text-white hover:bg-[var(--surface-2)] hover:translate-x-[2px]"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive
                      ? {
                          background:
                            "linear-gradient(180deg, #ffa64d 0%, var(--primary) 55%, #db6d10 100%)",
                          boxShadow:
                            "0 1px 0 rgba(255,255,255,0.35) inset, 0 10px 26px -10px rgba(245,130,32,0.55)",
                        }
                      : { transition: "transform 220ms cubic-bezier(0.16,1,0.3,1), background 200ms ease" }
                  }
                >
                  <n.icon className="w-5 h-5 shrink-0" />
                  {n.label}
                </NavLink>
              ))}
          </nav>
          {/* Fade hint at the bottom so users see there's more to scroll */}
          <div
            aria-hidden="true"
            className="pointer-events-none h-8 -mt-8 shrink-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.55) 60%, rgba(10,10,10,0.85) 100%)",
            }}
          />
        </aside>

        <main className="flex-1 max-w-[1600px] mx-auto w-full px-4 md:px-8 py-6 md:py-10">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -4, filter: "blur(2px)" }}
              transition={{ duration: 0.34, ease: [0.16, 1, 0.3, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile bottom nav — glass */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 no-print topbar-glass"
        data-testid="mobile-nav"
      >
        <div className="grid" style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
          {nav
            .filter((n) => ["/", "/insights", "/handovers", "/breakdowns", "/ppfs"].includes(n.to))
            .filter((n) => !n.cap || can(user, n.cap))
            .map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`${n.testid}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2.5 gap-1 text-[10px] uppercase font-black tracking-widest ${
                    isActive ? "text-black" : "text-white"
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? {
                        background:
                          "linear-gradient(180deg, #ffa64d 0%, var(--primary) 55%, #db6d10 100%)",
                        boxShadow:
                          "0 1px 0 rgba(255,255,255,0.35) inset",
                      }
                    : {}
                }
              >
                <n.icon className="w-5 h-5" />
                {n.label}
              </NavLink>
            ))}
        </div>
      </nav>
      <div className="h-16 md:hidden" />

      <AIAssistant />
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}
